import { Button, Flex, Modal, Text } from '@conteg/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorageUnitDtoFragment } from 'types/generated/graphql';
import { useChangeReservationDurationMode } from 'utils/hooks/use-change-reservation-duration-mode';
import { formatDateTime, TimeFormats } from 'utils/time/format-time';

import ChangeReservationDuration from '../actions/change-reservation-duration/change-reservation-duration';
import ReservationLanguageDetail from '../actions/reservation-language-detail/reservation-language-detail';

type Props = { storageUnit: StorageUnitDtoFragment };

const FirstUpcomingReservationDetail = ({ storageUnit }: Props) => {
  const { t } = useTranslation();
  const r = storageUnit.firstUpcomingReservation;
  const [openModal, setOpenModal] = useState<boolean>(false);

  // The action here changes this upcoming reservation itself, so what decides is its own flag - not the
  // parcel occupying the compartment today (that exception belongs to the forced open). Shortening a
  // window whose parcel will need a recipient release code hands it to the courier before anyone can
  // enter that code, which is what the gateway refuses too.
  const isTwoFactorPending = !!r?.is2FaRequired && !r?.twoFactorVerifiedAt;

  const changeReservationDurationMode =
    useChangeReservationDurationMode(isTwoFactorPending);

  if (!r) return null;

  return (
    <Flex flexDirection="column" gap="1rem">
      {/* 1) Start & End */}
      <Text
        variant="large"
        content={t('Page.Reservation.Times', {
          start: formatDateTime(r.from, TimeFormats.dateTime),
          end: formatDateTime(r.to, TimeFormats.dateTime),
        })}
      />

      <Flex gap="4rem" alignItems="center">
        {/* 2) Limit for stock-in */}
        <Text
          variant="large"
          content={t('Page.Reservation.TimesStockIn', {
            start: formatDateTime(r.expiration, TimeFormats.dateTime),
          })}
        />
        {changeReservationDurationMode && (
          <>
            <Button
              testId="button-open-modal-update-reservation-duration"
              variant="primary"
              size="s"
              title={t(
                changeReservationDurationMode === 'bidirectional'
                  ? 'Button.ChangeReservationDuration'
                  : 'Button.UpdateReservationDuration'
              )}
              onClick={() => setOpenModal(true)}
            />
            <Modal
              testId="unit-actions-modal"
              size="l"
              showCloseButton={true}
              isOpen={openModal}
              onClose={() => setOpenModal(false)}
            >
              <ChangeReservationDuration
                reservationId={r.id}
                from={r.from}
                to={r.to}
                mode={changeReservationDurationMode}
              />
            </Modal>
          </>
        )}
      </Flex>

      {/* 3) Limit for handover */}
      <Text
        variant="large"
        content={t('Page.Reservation.TimeHandover', {
          time: formatDateTime(r.to, TimeFormats.dateTime),
        })}
      />

      {/* 4) Language */}
      {r.languageCode && (
        <ReservationLanguageDetail languageCode={r.languageCode} />
      )}

      {/* 5) Service type */}
      <Text
        variant="large"
        content={`${t('Page.Reservation.ServiceType')}: ${t(
          `Page.Reservation.ServiceType.${r.serviceType}`
        )}`}
      />

      {/* 6) Two-factor verification */}
      <Text
        testId="two-factor-requirement"
        variant="large"
        content={
          <>
            <strong>{t('Page.Reservation.TwoFactor')}: </strong>
            {t(
              r.is2FaRequired
                ? 'Page.Reservation.TwoFactor.Yes'
                : 'Page.Reservation.TwoFactor.No'
            )}
          </>
        }
      />

      {/* 7) Company & contact */}
      <Text
        variant="large"
        content={
          <>
            <strong>{t('Page.Reservation.Company')}:</strong>
            {t('Page.Reservation.Company.Info', {
              name: storageUnit.company?.name,
              contact: storageUnit.phoneNumber,
            })}
          </>
        }
      />
    </Flex>
  );
};

export default FirstUpcomingReservationDetail;
